/**
 * Keyboard Shortcuts Panel
 * Displays all available keyboard shortcuts in the application
 */
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Keyboard, Command, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SHORTCUT_CATEGORIES = [
  {
    name: 'Transport',
    shortcuts: [
      { keys: ['Space'], description: 'Play / Pause' },
      { keys: ['Enter'], description: 'Stop and return to start' },
      { keys: ['R'], description: 'Start / Stop Recording' },
      { keys: ['L'], description: 'Toggle Loop' },
    ],
  },
  {
    name: 'Navigation',
    shortcuts: [
      { keys: ['Home'], description: 'Go to start' },
      { keys: ['End'], description: 'Go to end' },
      { keys: ['+', '='], description: 'Zoom in' },
      { keys: ['-'], description: 'Zoom out' },
      { keys: ['0'], description: 'Fit to window' },
    ],
  },
  {
    name: 'Editing',
    shortcuts: [
      { keys: ['Ctrl', 'Z'], description: 'Undo' },
      { keys: ['Ctrl', 'Shift', 'Z'], description: 'Redo' },
      { keys: ['Ctrl', 'C'], description: 'Copy' },
      { keys: ['Ctrl', 'V'], description: 'Paste' },
      { keys: ['Ctrl', 'X'], description: 'Cut' },
      { keys: ['Ctrl', 'A'], description: 'Select all' },
      { keys: ['Delete'], description: 'Delete selected' },
      { keys: ['Ctrl', 'D'], description: 'Duplicate' },
    ],
  },
  {
    name: 'Tracks',
    shortcuts: [
      { keys: ['Ctrl', 'N'], description: 'New track' },
      { keys: ['M'], description: 'Mute selected track' },
      { keys: ['S'], description: 'Solo selected track' },
      { keys: ['Up', 'Down'], description: 'Navigate tracks' },
    ],
  },
  {
    name: 'Piano Roll',
    shortcuts: [
      { keys: ['V'], description: 'Select tool' },
      { keys: ['P'], description: 'Pencil / Draw tool' },
      { keys: ['E'], description: 'Erase tool' },
      { keys: ['Q'], description: 'Quantize selected notes' },
      { keys: ['Esc'], description: 'Close piano roll' },
      { keys: ['Alt', 'Drag'], description: 'Duplicate note' },
    ],
  },
  {
    name: 'Project',
    shortcuts: [
      { keys: ['Ctrl', 'S'], description: 'Save project' },
      { keys: ['Ctrl', 'Shift', 'E'], description: 'Export audio' },
      { keys: ['?'], description: 'Show shortcuts (this panel)' },
    ],
  },
];

export const KeyboardShortcutsPanel = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCategories, setFilteredCategories] = useState(SHORTCUT_CATEGORIES);

  // Filter shortcuts based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCategories(SHORTCUT_CATEGORIES);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = SHORTCUT_CATEGORIES.map(category => ({
      ...category,
      shortcuts: category.shortcuts.filter(shortcut =>
        shortcut.description.toLowerCase().includes(query) ||
        shortcut.keys.some(key => key.toLowerCase().includes(query))
      ),
    })).filter(category => category.shortcuts.length > 0);

    setFilteredCategories(filtered);
  }, [searchQuery]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[700px] md:max-h-[80vh] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden"
            data-testid="keyboard-shortcuts-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <Keyboard className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Keyboard Shortcuts</h2>
                  <p className="text-xs text-zinc-400">Press ? anywhere to open this panel</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Search */}
            <div className="px-6 py-3 border-b border-zinc-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search shortcuts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500/50"
                  data-testid="shortcut-search"
                />
              </div>
            </div>

            {/* Shortcuts List */}
            <div className="flex-1 overflow-y-auto p-6">
              {filteredCategories.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  No shortcuts found for "{searchQuery}"
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredCategories.map((category) => (
                    <div key={category.name} className="space-y-2">
                      <h3 className="text-sm font-medium text-orange-400 uppercase tracking-wide">
                        {category.name}
                      </h3>
                      <div className="space-y-1">
                        {category.shortcuts.map((shortcut, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-zinc-800/50 transition-colors"
                          >
                            <span className="text-sm text-zinc-300">{shortcut.description}</span>
                            <div className="flex items-center gap-1">
                              {shortcut.keys.map((key, kidx) => (
                                <span key={kidx} className="flex items-center">
                                  {kidx > 0 && <span className="text-zinc-600 mx-0.5">+</span>}
                                  <kbd className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs font-mono text-zinc-200">
                                    {key === 'Ctrl' ? (
                                      <span className="flex items-center gap-0.5">
                                        <Command className="w-3 h-3" />
                                      </span>
                                    ) : key}
                                  </kbd>
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-zinc-700 bg-zinc-800/50">
              <p className="text-xs text-zinc-500 text-center">
                Tip: Most shortcuts work in the DAW workspace. Piano Roll shortcuts are active when the editor is open.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default KeyboardShortcutsPanel;
